import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Heart, Check, Loader2, ArrowLeft, Sparkles, FileDown, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUCALYPTUS = "https://images.pexels.com/photos/8903651/pexels-photo-8903651.jpeg";

const PLAN_META = {
  pomlp_monthly: { blurb: "Flexible month-to-month access.", featured: false },
  pomlp_yearly: { blurb: "Best value — save vs. monthly.", featured: true },
  pomlp_lifetime: { blurb: "Pay once, keep forever.", featured: false },
};

const INCLUDED = [
  "All 9 legacy sections & worksheets",
  "Compassionate AI writing assistant",
  "Beautiful PDF export of your legacy",
  "Secure cloud saving across devices",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    axios.get(`${API}/payments/plans`).then(({ data }) => setPlans(data.plans)).catch(() => toast.error("Could not load plans. Please refresh."));
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/register");
    else if (user.is_paid) navigate("/planner");
  }, [user, loading, navigate]);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-ivory relative overflow-hidden">
      <div className="eucalyptus-corner top-0 left-0" style={{ backgroundImage: `url(${EUCALYPTUS})`, transform: "scaleX(-1)" }} />
      <div className="eucalyptus-corner bottom-0 right-0" style={{ backgroundImage: `url(${EUCALYPTUS})` }} />

      <div className="max-w-5xl mx-auto px-6 py-14 relative z-10">
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-navy/60 hover:text-navy transition-colors mb-8" data-testid="pricing-back-btn">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>

        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={LOGO_URL} alt="The Ever Grace Group" className="h-14 w-14 rounded-full object-cover" />
            <span className="font-serif text-2xl text-navy"><span className="text-navy">Ever</span> <span className="text-gold">Grace</span></span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-navy mb-4" data-testid="pricing-title">
            Unlock your legacy
          </h1>
          <p className="text-charcoal/75 text-lg">
            Choose the plan that suits you. Every option includes the complete Peace of Mind Legacy experience.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {plans.map((p, i) => {
            const meta = PLAN_META[p.lookup_key] || {};
            return (
              <motion.div key={p.lookup_key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                data-testid={`plan-card-${p.lookup_key}`}
                className={`relative bg-white rounded-md p-8 flex flex-col border transition-[border-color,box-shadow] ${
                  meta.featured ? "border-gold shadow-[0_20px_50px_rgba(212,175,55,0.18)]" : "border-gold/20"
                }`}>
                {meta.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                    Best value
                  </span>
                )}
                <div className="flex items-center gap-2 mb-1">
                  {p.recurring ? <Sparkles className="w-4 h-4 text-sage" /> : <InfinityIcon className="w-4 h-4 text-sage" />}
                  <h3 className="font-serif text-2xl text-navy">{p.label}</h3>
                </div>
                <p className="font-serif text-4xl text-navy mt-2 mb-1">{p.price}</p>
                <p className="text-sm text-charcoal/60 mb-6">{meta.blurb}</p>
                <Button onClick={() => checkout(p.lookup_key)} disabled={!!busy} data-testid={`checkout-btn-${p.lookup_key}`}
                  className={`w-full rounded-full py-6 font-semibold ${
                    meta.featured ? "bg-gold hover:bg-gold/90 text-navy" : "bg-navy hover:bg-navy/90 text-ivory"
                  }`}>
                  {busy === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {p.recurring ? "Subscribe" : "Buy lifetime"}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <div className="max-w-md mx-auto bg-white/60 border border-gold/20 rounded-md p-8">
          <p className="font-serif text-xl text-navy mb-4 text-center">Every plan includes</p>
          <ul className="space-y-3">
            {INCLUDED.map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-charcoal/80">
                <span className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-navy" strokeWidth={2.5} />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-center text-xs text-charcoal/50 mt-8">
          Secure checkout by Stripe. Test card: 4242 4242 4242 4242, any future expiry & CVC.
        </p>
      </div>
    </div>
  );
}
